import styles from './Table.module.scss';

import { Close } from '@mui/icons-material';
import {
  Fab,
  LinearProgress,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Divider from '@mui/material/Divider';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { actions as tableActions, TableInstance } from 'react-table';

import {
  ICssClasses,
  RowsLength,
} from '@work4all/components/lib/dataDisplay/basic-table/types';
import {
  VirtualizedTableV8,
  VirtualizedTableV8Props,
} from '@work4all/components/lib/dataDisplay/basic-table/VirtualizedTableV8';
import { PrintComponent } from '@work4all/components/lib/layout/print-component/PrintComponent';

import { usePermissions } from '@work4all/data';

import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { AreaConfig } from '@work4all/models/lib/Tables/Tables';

import { ListPageContext } from '../../../containers/file-entities-lists/list-page-context';
import { settings, useSetting } from '../../../settings';
import { ControlViewBottom } from '../control-view-bottom/ControlViewBottom';
import { ControlViewCenter } from '../control-view-center/ControlViewCenter';
import { ControlViewLeft, ControlViewLeftProps } from '../control-view-left';
import { TableNoRowsPlaceholderV8 } from '../TableNoRowsPlaceholderV8';

import { TableContextMenuV8 } from './components/context-menu/TableContextMenuV8';
import {
  Board,
  BoardProps,
  isBoardEnabled,
} from './components/kanban-board/Board';
import { TableRightArea } from './components/table-right-area/TableRightArea';
import { IToolBar } from './components/toolbar/ToolBar';
import { ToolbarV8 } from './components/toolbar/ToolBarV8';
import { ToolbarDndWrapperV8 } from './components/toolbar-dnd-wrapper/components/toolbar-dnd-wrapper-v8';
import { useGroupingToolbarActionsV8 } from './grouping/hooks/use-grouping-toolbar-actions-v8';

export type BottomAreaSize = 'contained' | 'fill';

export interface TableAreas {
  left?: Pick<AreaConfig, 'content'> &
    Pick<
      ControlViewLeftProps,
      'title' | 'custom' | 'collapsible' | 'resizable' | 'actions'
    >;
  right?: AreaConfig;
  bottom?: AreaConfig & { size?: BottomAreaSize };
  center?: AreaConfig;
  top?: AreaConfig;
}

export interface EntityTableLayoutV8Props
  extends Pick<
      VirtualizedTableV8Props,
      | 'table'
      | 'cardConfig'
      | 'scrollRef'
      | 'fetchMore'
      | 'displayFooter'
      | 'pending'
      | 'noRowsRenderer'
      | 'rowHeightRem'
      | 'onRowClick'
    >,
    Pick<IToolBar, 'actions'> {
  areas?: TableAreas;
  /**
   * @default "table"
   */
  layout?: 'table' | 'cards' | 'board';

  onRowDoubleClick?: (id: string, row?: any) => boolean;

  hideToolbar?: boolean;
  classes?: {
    root?: string;
  };

  basicClasses?: ICssClasses;
  hideMoreButton?: boolean;
  hideRightActions?: boolean;
  hideSearch?: boolean;
  listEntityType?: Entities;

  board?: BoardProps;
  /* Whether or not the table is rendered within the "Pick from List" modal.
    When it is, parts of the table UI will be changed to make it easier to
    select a row. */
  isSelectionList?: boolean;
  total?: number | RowsLength;
  footerCellProps?: Record<string, number>;
}

export function EntityTableLayoutV8(props: EntityTableLayoutV8Props) {
  const {
    layout: layoutProp = 'table',
    hideToolbar = false,
    cardConfig,
    hideMoreButton = false,
    hideRightActions = false,
    hideSearch = false,
    listEntityType,
    table,
    total,
  } = props;

  const isSmUp = useMediaQuery<Theme>((t) => t.breakpoints.up('sm'));
  const isMdUp = useMediaQuery<Theme>((t) => t.breakpoints.up('md'));
  const isLgUp = useMediaQuery<Theme>((t) => t.breakpoints.up('xl'));

  // Preview should be disabled on mobile when opening the table from the
  // "Pick from List" modal as it makes it impossible to confirm selection
  // with the current UI.
  const shouldEnablePreview = isSmUp || !props.isSelectionList;

  const tableInstanceRef = useRef<TableInstance>(null);

  const listPageContext = useContext(ListPageContext);

  const leftArea = props.areas?.left;
  const rightArea = props.areas?.right;
  const bottomArea = props.areas?.bottom;
  const centerArea = props.areas?.center;
  const topArea = props.areas?.top;

  const tablesLayoutBorders = useSetting(settings.tablesLayoutBorders());

  const entityType = listEntityType ?? listPageContext?.entityType;

  const layout =
    isBoardEnabled(entityType) || layoutProp !== 'board' ? layoutProp : 'table';

  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { canEdit } = usePermissions();

  const noRowsRenderer = () => <TableNoRowsPlaceholderV8 table={table} />;

  const onLocalRowDoubleClick = useCallback(
    (id) => {
      if (!entityType) return;

      if (!canEdit({ entity: entityType, record: { id } })) {
        const entity = entityType.toUpperCase();
        const entityTranslation = i18n.exists(`MORE.${entity}`)
          ? t(`MORE.${entity}`)
          : t(`MORE.${entity}S`);

        enqueueSnackbar(
          t('ERROR.MISSING_RIGHTS', {
            entity: entityTranslation,
          }),
          { variant: 'error', autoHideDuration: 6000 }
        );
        return;
      }
    },
    [entityType, canEdit, i18n, t, enqueueSnackbar]
  );

  const handleRowDoubleClick = (id: string) => {
    const entityId = id.substring(id.lastIndexOf('.') + 1);

    if (props.onRowDoubleClick) {
      const row = table.getRow(id);
      const consumed = props.onRowDoubleClick(entityId, row.original);
      if (!consumed) onLocalRowDoubleClick(entityId);
    } else if (props.actions?.edit?.handler) {
      props.actions?.edit?.handler(entityId);
    } else {
      onLocalRowDoubleClick(entityId);
    }
  };

  const groupingToolbarActions = useGroupingToolbarActionsV8({ table, layout });
  const toolbarActions: IToolBar['actions'] = useMemo(() => {
    return {
      ...props.actions,
      custom: {
        ...props.actions?.custom,
        left: [
          ...(props.actions?.custom?.left ?? []),
          ...groupingToolbarActions,
        ],
      },
    };
  }, [groupingToolbarActions, props.actions]);

  return (
    <PrintComponent>
      {props.pending && (
        <LinearProgress
          data-test-id="loading-progress-indicator"
          className={styles.progressBar}
        />
      )}
      <div
        className={clsx(styles.root, props.classes?.root)}
        data-test-id="table"
      >
        <div className={styles.right}>
          {!hideToolbar && (
            <ToolbarDndWrapperV8 table={table}>
              <ToolbarV8
                hideLayoutSelect={!cardConfig}
                hideMoreButton={hideMoreButton}
                hideRightActions={hideRightActions}
                mobile={!isLgUp}
                actions={toolbarActions}
                listEntityType={entityType}
                hideSearch={hideSearch}
                table={table}
                total={layout === 'board' ? null : total}
              />
            </ToolbarDndWrapperV8>
          )}
          <Divider orientation="horizontal" />
          <div className={styles.rightRoot}>
            {leftArea && isMdUp ? (
              <>
                <ControlViewLeft {...leftArea} entity={entityType}>
                  {leftArea.content}
                </ControlViewLeft>
                <Divider orientation="vertical" />
              </>
            ) : null}
            <div
              className={clsx(styles.rightBody, {
                [styles['rightBody--split']]: !!bottomArea,
                [styles['rightBody--horizontal-split']]: !!centerArea,
              })}
            >
              {isLgUp &&
                tableInstanceRef.current?.selectedFlatRows.length > 1 && (
                  <Fab
                    variant="extended"
                    className={styles.resetSelectionFab}
                    size="medium"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      tableInstanceRef?.current?.dispatch({
                        type: tableActions.resetSelectedRows,
                      });
                    }}
                  >
                    <Close sx={{ mr: 1 }} />
                    <Typography variant="body1">
                      {t('COMMON.DESELECT')}
                    </Typography>
                  </Fab>
                )}

              <ControlViewCenter
                entityType={entityType}
                resizable={!!centerArea?.content}
              >
                <TableContextMenuV8 table={table} actions={props.actions}>
                  {({ onRowContextMenu }) => {
                    return (
                      <>
                        {layout === 'board' && <Board {...props.board} />}
                        <div
                          className={clsx(styles.tableWrapper, {
                            [styles.height100]: !!centerArea?.content,
                            [styles.board]: layout === 'board',
                          })}
                        >
                          {topArea ? topArea.content : null}

                          <VirtualizedTableV8
                            table={table}
                            scrollRef={props.scrollRef}
                            pending={props.pending}
                            noRowsRenderer={noRowsRenderer}
                            cardsView={layout === 'cards'}
                            bottomPadding={isLgUp ? null : '5rem'}
                            cardConfig={cardConfig}
                            fetchMore={props.fetchMore}
                            className={styles.table}
                            onRowClick={props.onRowClick}
                            onRowDoubleClick={handleRowDoubleClick}
                            onRowContextMenu={onRowContextMenu}
                            displayFooter={props.displayFooter}
                            classes={{
                              ...props.basicClasses,
                              cell: clsx(props.basicClasses?.cell, {
                                [styles.borderVertical]:
                                  tablesLayoutBorders.value.vertical,
                              }),
                              row: clsx(props.basicClasses?.row, {
                                [styles.borderHorizontal]:
                                  tablesLayoutBorders.value.horizontal,
                              }),
                            }}
                            rowHeightRem={props.rowHeightRem}
                            footerCellProps={props.footerCellProps}
                          />
                        </div>
                      </>
                    );
                  }}
                </TableContextMenuV8>
              </ControlViewCenter>

              {centerArea ? centerArea.content : null}
              {bottomArea &&
                (!bottomArea.size || bottomArea.size === 'contained') && (
                  <ControlViewBottom
                    entityType={entityType}
                    resizable={bottomArea.resizable}
                  >
                    {bottomArea.content}
                  </ControlViewBottom>
                )}
            </div>
            {shouldEnablePreview && rightArea && rightArea.content && (
              <TableRightArea rightArea={rightArea} entityType={entityType} />
            )}
          </div>
          {bottomArea && bottomArea.size === 'fill' && (
            <>
              <Divider />
              <ControlViewBottom
                entityType={entityType}
                resizable={bottomArea.resizable}
              >
                {bottomArea.content}
              </ControlViewBottom>
            </>
          )}
        </div>
      </div>
    </PrintComponent>
  );
}
